import { useState } from 'react';
import { useReduxState } from 'hooks';
import { filterValuesSeparator } from 'shared-helpers/consts';
import api from 'api';
import { useApiData } from 'lib/api';
import { LoadingIndicator, Pagination } from 'components/common';
import { CoursesTile, CoursesWrapper } from 'components/courses';
import { PageEmptyIndicator, PageSectionHeader } from 'components/page';

import type { GetCoursesRes } from 'shared-types/responses';

const HomeGuestCourses = () => {
  const { messages, user } = useReduxState();

  const [page, setPage] = useState(1);
  const [progressByCourseId, setProgressByCourseId] = useState({});

  const limitCoursesPerPage = 12;

  const { data, loading, updateData } = useApiData<GetCoursesRes>({
    onDataUpdated: (data) => {
      if (!user || data?.numberOfCourses < 1) return;
      updateProgresses(data?.courses);
    },

    url:
      `/admin/courses?courseType=available` +
      `&limit=${limitCoursesPerPage}` +
      `&page=${page}`
  });

  const updateProgresses = async (updatedCourses) => {
    try {
      const courseIds = updatedCourses
        // exclude courses without a course completion with a valid status
        .filter((course) => course.courseCompletion?.status)
        .map((course) => course.id);

      if (!courseIds.length) {
        setProgressByCourseId({});
        return;
      }

      const response = await api.get(
        `/admin/users/${user.id}/courses` +
          `?courseIds=${courseIds.join(filterValuesSeparator)}` +
          `&limit=${courseIds.length}`
      );

      const newProgresses = {};
      response.data.courses.forEach((course) => {
        newProgresses[course.id] = course.progressData;
      });
      setProgressByCourseId(newProgresses);
    } catch (error) {
      setProgressByCourseId({});
    }
  };

  return loading ? (
    <LoadingIndicator />
  ) : data?.numberOfCourses > 0 ? (
    <>
      <PageSectionHeader title={messages.commonCourses} />

      <CoursesWrapper isHomeView={true}>
        {data?.courses.map((course) => (
          <div className="course" key={course.id}>
            <CoursesTile
              course={course}
              progressData={progressByCourseId[course.id]}
              updateCourses={updateData}
            />
          </div>
        ))}
      </CoursesWrapper>

      <Pagination
        currentPage={page}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page }) => setPage(page)}
      />
    </>
  ) : (
    <>
      <PageSectionHeader title={messages.commonCourses} />

      <PageEmptyIndicator
        icon="book"
        title={messages.coursesNoCoursesFound}
        description={messages.coursesNoCoursesActive}
      />
    </>
  );
};

export default HomeGuestCourses;
